import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, User } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const Profile = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      if (data?.token) {
        localStorage.setItem('token', data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }

      toast.success('Password changed successfully! Other sessions have been signed out.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard" data-testid="profile-page">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }} data-testid="page-title">
            Profile Settings
          </h1>
        </div>

        <div style={{ maxWidth: '800px' }}>
          {/* User Info Section */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <User size={24} style={{ color: '#667eea' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Account Information</h2>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <Label>Name</Label>
                <p style={{ marginTop: '8px', fontSize: '16px', fontWeight: '500' }} data-testid="user-name-display">
                  {user?.name}
                </p>
              </div>
              <div>
                <Label>Email</Label>
                <p style={{ marginTop: '8px', fontSize: '16px', fontWeight: '500' }} data-testid="user-email-display">
                  {user?.email}
                </p>
              </div>
              <div>
                <Label>Role</Label>
                <span 
                  className={`status-badge ${user?.role === 'admin' ? 'status-paid' : 'status-sent'}`}
                  style={{ marginTop: '8px', display: 'inline-block' }}
                  data-testid="user-role-display"
                >
                  {user?.role}
                </span>
              </div>
            </div>
          </div>

          {/* Change Password Section */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <Lock size={24} style={{ color: '#667eea' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Change Password</h2>
            </div>

            <form onSubmit={handlePasswordChange} data-testid="password-change-form">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="form-group">
                  <Label htmlFor="currentPassword">Current Password *</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid="current-password-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="newPassword">New Password *</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                    Password must be at least 6 characters
                  </p>
                </div>

                <div className="form-group">
                  <Label htmlFor="confirmPassword">Confirm New Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  style={{ width: 'fit-content' }}
                  data-testid="change-password-button"
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
